import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getPragueDate } from '../utils/pragueDate';
import { DateService } from '../utils/DateService';

/**
 * Returns a boolean array [Mon, Tue, Wed, Thu, Fri, Sat, Sun] 
 * indicating if the user had qualifying activity on each day of the current week.
 * Uses Europe/Prague timezone for day calculation.
 */
export async function getWeeklyActivityStatus(userId: string): Promise<boolean[]> {
    try {
        // 1. Determine the current week's range (Mon-Sun) in Prague context
        // We use getPragueDate() to get today's YYYY-MM-DD string
        const todayPrague = getPragueDate(DateService.now());

        // Parse it back to a date object to do math
        // We treat the string as midnight to avoid timezone shifts during calculation
        const current = new Date(todayPrague);
        const day = current.getDay(); // 0 is Sunday

        // Adjust to make Monday index 0, Sunday index 6
        const diffToMonday = day === 0 ? -6 : 1 - day;

        const monday = new Date(current);
        monday.setDate(current.getDate() + diffToMonday);

        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);

        // Format range strings
        // We can't use getPragueDate on these constructed dates simply because 
        // the constructing logic above might have shifted them across DST boundaries if not careful.
        // simpler approach: iterate 0..6 adding days to mondayString

        const weekDates: string[] = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(monday);
            d.setDate(monday.getDate() + i);
            // We use the same formatter logic as getPragueDate but on this local date 
            // effectively treating it as the date we want
            // Actually simplest is to reconstruct the YYYY-MM-DD manually or use the known offset
            // But since we have getPragueDate which takes a Date, let's just use it on a Date that is noon that day
            // to be safe from midnight offsets.
            d.setHours(12, 0, 0, 0);
            weekDates.push(getPragueDate(d));
        }

        const startStr = weekDates[0];
        const endStr = weekDates[6];

        // 2. Query Firestore
        // Collection: users/{uid}/user_activity_log
        // Filter: qualifies_for_streak == true
        // Filter: date >= startStr && date <= endStr
        // Note: date field in activity log is typically YYYY-MM-DD (checking implementation is good but let's assume standard)

        const logsRef = collection(db, 'users', userId, 'user_activity_log');
        const q = query(
            logsRef,
            where('qualifies_for_streak', '==', true),
            where('date', '>=', startStr),
            where('date', '<=', endStr)
        );

        const snapshot = await getDocs(q);
        const activeDates = new Set<string>();

        snapshot.forEach(doc => {
            const data = doc.data();
            if (data.date) {
                activeDates.add(data.date);
            }
        });

        // 3. Map to boolean array [Mon..Sun]
        return weekDates.map(dateStr => activeDates.has(dateStr));

    } catch (error) {
        console.error('Error fetching weekly activity:', error);
        // Return all false on error to avoid breaking UI
        return [false, false, false, false, false, false, false];
    }
}
